import {
  AddIconData,
  CloseIconData,
  PauseIconData,
  PlayArrowIconData,
  RestartAltIconData,
  StopIconData,
  TimerIconData,
  VolumeOffIconData,
  VolumeUpIconData,
} from "@meiden-connect/material-symbols";
import { Button, Icon } from "@meiden-connect/ui";
import { useShowToast } from "../notice/use-show-toast";
import {
  CANVAS_TIMER_COMMAND_RETRY_WINDOW_MS,
  CANVAS_TIMER_MAX_DURATION_MS,
  getCanvasTimerRemainingMs,
  normalizeCanvasTimerDuration,
  type CanvasTimerCommand,
} from "@refinement-canvas/domain";
import { customAlphabet } from "nanoid";
import { useEffect, useRef, useState } from "react";
import { useRealtimeClient } from "../../../realtime/realtime-context";
import { useCtrlWheelZoom } from "../input/use-ctrl-wheel-zoom";
import {
  loadCanvasTimerDuration,
  loadCanvasTimerVolume,
  saveCanvasTimerDuration,
  saveCanvasTimerVolume,
} from "./canvas-timer-preference";
import { useCanvasTimerStore } from "./canvas-timer-store";

const TIMER_TICK_INTERVAL_MS = 250;
const TIMER_COMMAND_CONFIRM_INTERVAL_MS = 5_000;
const TIMER_CHIME_URL = "/sounds/canvas-timer-chime.wav";
const generateTimerCommandId = customAlphabet(
  "23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ",
  16
);

/** キャンバス右上へ常設する共有タイマー。 */
export function CanvasTimer(): JSX.Element {
  const realtime = useRealtimeClient();
  const sharedTimer = useCanvasTimerStore((state) => state.timer);
  const hasReceivedInitialTimerSync = useCanvasTimerStore(
    (state) => state.hasReceivedInitialTimerSync
  );
  const pendingCommand = useCanvasTimerStore((state) => state.pendingCommand);
  const serverClockOffsetMs = useCanvasTimerStore((state) => state.serverClockOffsetMs);
  const beginTimerSync = useCanvasTimerStore((state) => state.beginTimerSync);
  const applyOptimisticCommand = useCanvasTimerStore((state) => state.applyOptimisticCommand);
  const rejectCommand = useCanvasTimerStore((state) => state.rejectCommand);
  const showToast = useShowToast();
  const [configuredDurationMs, setConfiguredDurationMs] = useState(loadCanvasTimerDuration);
  const [nowEpochMs, setNowEpochMs] = useState(Date.now);
  const [open, setOpen] = useState(false);
  const [volume, setVolume] = useState(loadCanvasTimerVolume);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | undefined>(undefined);
  const notifiedRevisionRef = useRef<number | undefined>(undefined);
  const previousRemainingMsRef = useRef<number | undefined>(undefined);
  useCtrlWheelZoom(rootRef);

  const currentServerEpochMs = nowEpochMs + serverClockOffsetMs;
  const sharedRemainingMs = sharedTimer
    ? getCanvasTimerRemainingMs(sharedTimer, currentServerEpochMs)
    : configuredDurationMs;
  const timerIsShared = sharedTimer?.status === "paused" || sharedTimer?.status === "running";
  const remainingMs = timerIsShared ? sharedRemainingMs : configuredDurationMs;
  const timerIsRunning = sharedTimer?.status === "running" && sharedRemainingMs > 0;
  const timerIsPaused = sharedTimer?.status === "paused";
  const timerIsFinished = sharedTimer?.status === "running" && sharedRemainingMs <= 0;

  useEffect(() => {
    const audio = new Audio(TIMER_CHIME_URL);
    audio.preload = "auto";
    audio.volume = volume;
    audioRef.current = audio;
    return () => {
      audio.pause();
      audioRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // ブラウザの自動再生制限に備え、最初の利用者操作で無音再生して終了音を利用可能にする。
  useEffect(() => {
    const unlockAudio = (): void => {
      const audio = audioRef.current;
      if (!audio) return;
      const configuredVolume = audio.volume;
      audio.volume = 0;
      void audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = configuredVolume;
          window.removeEventListener("pointerdown", unlockAudio, true);
          window.removeEventListener("keydown", unlockAudio, true);
        })
        .catch(() => {
          audio.volume = configuredVolume;
        });
    };
    window.addEventListener("pointerdown", unlockAudio, true);
    window.addEventListener("keydown", unlockAudio, true);
    return () => {
      window.removeEventListener("pointerdown", unlockAudio, true);
      window.removeEventListener("keydown", unlockAudio, true);
    };
  }, []);

  useEffect(() => {
    if (!timerIsRunning) return undefined;
    const interval = window.setInterval(() => setNowEpochMs(Date.now()), TIMER_TICK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [timerIsRunning]);

  // 保存失敗時の拒否通知まで失われても復帰できるよう、期限内は同じ操作を冪等再送する。
  // 期限到達時は楽観状態を戻して権威状態を再取得し、サーバの操作 ID 保持期間を超えて再送しない。
  useEffect(() => {
    if (!pendingCommand) return undefined;
    const retryDeadlineEpochMs =
      pendingCommand.createdAtEpochMs + CANVAS_TIMER_COMMAND_RETRY_WINDOW_MS;
    let timeoutId: number | undefined;
    const retryOrExpire = (): void => {
      const remainingRetryMs = retryDeadlineEpochMs - Date.now();
      if (remainingRetryMs <= 0) {
        if (rejectCommand(pendingCommand.commandId)) {
          // 権威状態の取得が完了するまで、ロールバック先を基準に次の操作を始めない。
          beginTimerSync();
          realtime.sendMessage({ type: "timer.request" });
          showToast(
            "warning",
            "タイマー操作の確認がタイムアウトしたため、最新の共有状態へ戻しました。"
          );
        }
        return;
      }
      realtime.sendMessage({
        type: "timer.command",
        command: pendingCommand.command,
        commandId: pendingCommand.commandId,
      });
      realtime.sendMessage({
        type: "timer.request",
        pendingCommandId: pendingCommand.commandId,
      });
      timeoutId = window.setTimeout(
        retryOrExpire,
        Math.min(TIMER_COMMAND_CONFIRM_INTERVAL_MS, remainingRetryMs)
      );
    };
    timeoutId = window.setTimeout(
      retryOrExpire,
      Math.min(TIMER_COMMAND_CONFIRM_INTERVAL_MS, Math.max(0, retryDeadlineEpochMs - Date.now()))
    );
    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [pendingCommand, realtime, beginTimerSync, rejectCommand, showToast]);

  // 権威状態を受信した瞬間の現在時刻へ更新し、停止中からの開始通知でも古い描画時刻を使わない。
  useEffect(() => {
    if (sharedTimer) setNowEpochMs(Date.now());
  }, [serverClockOffsetMs, sharedTimer]);

  // 正の残り時間をこのタブで観測した revision が 0 へ到達したときだけ、一度チャイムを鳴らす。
  // 既に終了済みのタイマーへ後から参加した利用者には、過去の終了音を鳴らさない。
  useEffect(() => {
    if (!sharedTimer || sharedTimer.status !== "running") {
      previousRemainingMsRef.current = undefined;
      return;
    }
    const previousRemainingMs = previousRemainingMsRef.current;
    if (
      sharedRemainingMs <= 0 &&
      previousRemainingMs !== undefined &&
      previousRemainingMs > 0 &&
      notifiedRevisionRef.current !== sharedTimer.revision
    ) {
      notifiedRevisionRef.current = sharedTimer.revision;
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        void audio.play().catch((error: unknown) => {
          console.warn("[timer] 終了音を再生できませんでした", error);
        });
      }
    }
    previousRemainingMsRef.current = sharedRemainingMs;
  }, [sharedRemainingMs, sharedTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsidePointer = (event: PointerEvent): void => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", closeOnOutsidePointer, true);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const sendCommand = (command: CanvasTimerCommand): void => {
    if (!hasReceivedInitialTimerSync || pendingCommand) return;
    const commandId = generateTimerCommandId();
    applyOptimisticCommand({ command, commandId, localNowEpochMs: Date.now() });
    realtime.sendMessage({ type: "timer.command", command, commandId });
  };

  const updateConfiguredDuration = (durationMs: number): void => {
    const normalized = normalizeCanvasTimerDuration(durationMs);
    setConfiguredDurationMs(normalized);
    saveCanvasTimerDuration(normalized);
  };

  const addMinute = (): void => {
    if (timerIsShared) {
      sendCommand({ action: "add-minute" });
      return;
    }
    updateConfiguredDuration(Math.min(CANVAS_TIMER_MAX_DURATION_MS, configuredDurationMs + 60_000));
  };

  return (
    <div
      ref={rootRef}
      className="canvas-timer"
      data-focus-control
      aria-busy={pendingCommand !== undefined}
    >
      <Button
        type="button"
        variant="intent"
        className={`canvas-timer-trigger${timerIsRunning || timerIsPaused ? " is-active" : ""}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`タイマー ${formatTimerDuration(remainingMs)}`}
        title="タイマーを開く"
        onClick={() => setOpen((current) => !current)}
      >
        <Icon d={TimerIconData} />
        <span>{formatTimerDuration(remainingMs)}</span>
      </Button>
      {open ? (
        <section className="canvas-timer-panel" role="dialog" aria-label="共有タイマー設定">
          <header className="canvas-timer-panel-header">
            <div>
              <strong>タイマー</strong>
              <span>キャンバスを見ている全員に共有</span>
            </div>
            <Button
              type="button"
              variant="action"
              className="canvas-timer-icon-button"
              aria-label="タイマー設定を閉じる"
              title="閉じる"
              onClick={() => setOpen(false)}
            >
              <Icon d={CloseIconData} />
            </Button>
          </header>
          <label className="canvas-timer-volume">
            <Icon d={volume === 0 ? VolumeOffIconData : VolumeUpIconData} />
            <span className="visually-hidden">終了音の音量</span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={Math.round(volume * 100)}
              aria-label={`終了音の音量 ${Math.round(volume * 100)}%`}
              onChange={(event) => {
                const nextVolume = event.currentTarget.valueAsNumber / 100;
                setVolume(nextVolume);
                saveCanvasTimerVolume(nextVolume);
              }}
            />
            <output>{Math.round(volume * 100)}%</output>
          </label>
          <div className={`canvas-timer-display${timerIsFinished ? " is-finished" : ""}`}>
            {timerIsShared ? (
              <time aria-live="off">{formatTimerDuration(remainingMs)}</time>
            ) : (
              <TimerDurationEditor
                durationMs={configuredDurationMs}
                onChange={updateConfiguredDuration}
              />
            )}
          </div>
          <div className="canvas-timer-actions">
            <Button
              type="button"
              variant="action"
              disabled={!hasReceivedInitialTimerSync || pendingCommand !== undefined}
              onClick={addMinute}
            >
              <Icon d={AddIconData} /> 1分
            </Button>
            <div className="canvas-timer-primary-actions">
              {timerIsShared ? (
                <Button
                  type="button"
                  variant="action"
                  className="canvas-timer-round-button"
                  aria-label="タイマーをリセット"
                  title="リセット"
                  disabled={!hasReceivedInitialTimerSync || pendingCommand !== undefined}
                  onClick={() => sendCommand({ action: "reset" })}
                >
                  <Icon d={timerIsFinished ? RestartAltIconData : StopIconData} />
                </Button>
              ) : null}
              {timerIsRunning ? (
                <Button
                  type="button"
                  variant="action emphasis"
                  className="canvas-timer-round-button"
                  aria-label="タイマーを一時停止"
                  title="一時停止"
                  disabled={!hasReceivedInitialTimerSync || pendingCommand !== undefined}
                  onClick={() => sendCommand({ action: "pause" })}
                >
                  <Icon d={PauseIconData} />
                </Button>
              ) : timerIsPaused ? (
                <Button
                  type="button"
                  variant="action emphasis"
                  className="canvas-timer-round-button"
                  aria-label="タイマーを再開"
                  title="再開"
                  disabled={
                    !hasReceivedInitialTimerSync || remainingMs <= 0 || pendingCommand !== undefined
                  }
                  onClick={() => sendCommand({ action: "resume" })}
                >
                  <Icon d={PlayArrowIconData} />
                </Button>
              ) : timerIsFinished ? null : (
                <Button
                  type="button"
                  variant="action emphasis"
                  className="canvas-timer-round-button"
                  aria-label="タイマーを開始して全員に共有"
                  title="開始"
                  disabled={!hasReceivedInitialTimerSync || pendingCommand !== undefined}
                  onClick={() => sendCommand({ action: "start", durationMs: configuredDurationMs })}
                >
                  <Icon d={PlayArrowIconData} />
                </Button>
              )}
            </div>
          </div>
          <p className="visually-hidden" role="status" aria-live="assertive">
            {timerIsFinished ? "タイマーが終了しました" : ""}
          </p>
        </section>
      ) : null}
    </div>
  );
}

function TimerDurationEditor({
  durationMs,
  onChange,
}: {
  readonly durationMs: number;
  readonly onChange: (durationMs: number) => void;
}): JSX.Element {
  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const [minuteDraft, setMinuteDraft] = useState(String(minutes).padStart(2, "0"));
  const [secondDraft, setSecondDraft] = useState(String(seconds).padStart(2, "0"));

  useEffect(() => {
    setMinuteDraft(String(minutes).padStart(2, "0"));
    setSecondDraft(String(seconds).padStart(2, "0"));
  }, [minutes, seconds]);

  const update = (nextMinutes: number, nextSeconds: number): void => {
    const safeMinutes = Number.isFinite(nextMinutes) ? Math.min(99, Math.max(0, nextMinutes)) : 0;
    const safeSeconds = Number.isFinite(nextSeconds) ? Math.min(59, Math.max(0, nextSeconds)) : 0;
    onChange((safeMinutes * 60 + safeSeconds) * 1000);
  };

  const commitDraft = (): void => {
    update(Number(minuteDraft), Number(secondDraft));
  };

  return (
    <div className="canvas-timer-duration-editor" aria-label="タイマー時間">
      <input
        type="text"
        inputMode="numeric"
        maxLength={2}
        pattern="[0-9]*"
        value={minuteDraft}
        aria-label="タイマーの分"
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setMinuteDraft(event.currentTarget.value.replace(/\D/g, ""))}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
      <span aria-hidden="true">:</span>
      <input
        type="text"
        inputMode="numeric"
        maxLength={2}
        pattern="[0-9]*"
        value={secondDraft}
        aria-label="タイマーの秒"
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setSecondDraft(event.currentTarget.value.replace(/\D/g, ""))}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
    </div>
  );
}

export function formatTimerDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
